// Artefacts: E/R slots, one flat item each (no tiers, no upgrades — see docs/game-design.md's
// "Артефакты", re-decided 2026-09-23 after live play: three tiers made a player read instead of choose).
// Same shape as skills.ts — a data table plus CAST-style implementations — kept in its own module because
// game.ts is being edited by other subtasks in parallel; this file only *reads* Game/Hero, never
// restructures them.
import * as THREE from 'three';
import { LAYOUT, type Team } from './layout.js';
// RULES is a real (non-type) import from game.js, which itself imports this module — safe only because
// every read of it below happens inside a function body, never at this module's own top-level evaluation
// (a circular *value* read at eval time would hit RULES before game.ts's own export initializer has run).
import { RULES, type Game, type Hero } from './game.js';
import type { ArtifactLine, ArtifactSlotKey } from './commands.js';
import type { HeroId, Vec2 } from './events.js';
// Same circular-import rule as RULES above: isMonster/bulkOf are only ever read inside function bodies.
import { isMonster, isHero, bulkOf, type Target } from './monsters.js';

const flatDist = (a: { x: number; z: number }, b: { x: number; z: number }) => Math.hypot(a.x - b.x, a.z - b.z);
const isUnit = (t: unknown): t is Target => !!t && typeof t === 'object' && 'alive' in (t as object);
const toPoint = (t: Target | Vec2): THREE.Vector3 => (isUnit(t) ? t.pos.clone() : new THREE.Vector3(t.x, 0, t.z));
function dirTo(from: { x: number; z: number }, point: THREE.Vector3) {
  const d = new THREE.Vector3(point.x - from.x, 0, point.z - from.z);
  if (d.lengthSq() < 1e-4) d.set(0, 0, 1);
  return d.normalize();
}

/* ---------------- data table ---------------- */
// `kind: 'passive'` entries have no button, no cooldown and no targeting — just one small always-on
// modifier, read by the helper functions in the next section, never by CAST/canCastArtifact/castArtifact
// (those only ever handle `kind: 'active'`). `ai` (bot triggers for actives — see botArtifacts() at the
// bottom) follows SKILLS' own convention: a value where the trigger needs a genuine number of its own, a
// bare `true` where the condition is fully expressed by numbers the line already has elsewhere.
export const ARTIFACTS = {
  // ---- passives: small, always-on, no button (docs/game-design.md's own "намеренно мелкие") ----
  shoulder: { kind: 'passive', hp: 25 },
  whetstone: { kind: 'passive', atkSpeed: 0.12 },
  boots: { kind: 'passive', moveSpeed: 0.15, quiet: 5 },  // out of combat only, same rule as the salve's
  // "Вне боя" is explicit: 5s since this hero last TOOK damage, of any kind — see Game.damage()'s own
  // `lastDamageAt` update, which fires for direct hits and zone/DOT ticks alike, so a zone tickling a hero
  // every half-second cannot be sat in indefinitely while this quietly heals through it.
  salve: { kind: 'passive', regenPct: 0.02, quiet: 5 },  // share of max hp/s on top of base regen
  hourglass: { kind: 'passive', cdRate: 0.18 },       // Q/W and active E/R cooldowns run this much faster
  mistAmulet: { kind: 'passive', missChance: 0.15 },  // an enemy basic attack on the holder misses
  graveCandle: { kind: 'passive', respawnCut: 0.3 },  // the holder's respawn wait, this much shorter
  // +dmg to an enemy HERO whose hp, read before mitigation like Memento mori's, is above `above` of max.
  dragonTooth: { kind: 'passive', dmg: 0.1, above: 0.7 },
  // The holder's team captures the point he stands on this much faster; one bonus per point however many
  // banners stand on it. Contested points don't move at all, so it only ever speeds an uncontested capture.
  banner: { kind: 'passive', capture: 0.25 },
  // ---- actives: E or R, a button, a cooldown ----
  // Склянка крови charges instead of cooling down: its slot `cd` still runs down from `cd` to 0, and the
  // charge is the elapsed share of that times `heal` (the cap, a share of max hp) — see vialCharge(). It can
  // be drunk at any charge of at least `minCharge`. ai: drink under `low` hp once charged to `lowCharge`, or
  // under `panic` hp with anything from `panicCharge` up.
  bloodvial: { kind: 'active', targeting: 'instant', cd: 60, heal: 0.6, minCharge: 0.01, ai: { low: 0.5, lowCharge: 0.25, panic: 0.25, panicCharge: 0.05 } },
  windoil: { kind: 'active', targeting: 'instant', cd: 25, haste: 0.25, dur: 3, ai: { near: 8 } },
  cloak: { kind: 'active', targeting: 'instant', cd: 45, dur: 4, ai: { low: 0.4, chased: 5 } },
  // Totems: set down at the hero's feet, pulse every `interval` s for `pct` of each hero's max hp per second
  // within `r`, break after `hp` enemy basic attacks. One of each per hero — a recast replaces the old one.
  // ai: Стойкости wants an ally (self included) under `hurt` of max hp in a fight; Гнева an enemy within `near`.
  totemWard: { kind: 'active', targeting: 'instant', cd: 30, hp: 2, r: 6, life: 3, interval: 0.5, pct: 0.05, ai: { hurt: 0.75 } },
  totemWrath: { kind: 'active', targeting: 'instant', cd: 30, hp: 2, r: 6, life: 3, interval: 0.5, pct: 0.04, ai: { near: 5 } },
  brand: { kind: 'active', targeting: 'enemy', cd: 45, range: 7, pct: 0.15, dur: 4, ai: { withAlly: 6 } },
  tar: { kind: 'active', targeting: 'point', cd: 25, range: 8, r: 3, life: 4, slow: 0.35, healReduction: 0.5, ai: { onContested: true } },
} as const satisfies Record<ArtifactLine, any>;

/* ---------------- passive helpers ---------------- */
// One place each modifier is read from, so game.ts's hooks (speedOf, attack cooldown, respawn/heal caps,
// the out-of-combat regen tick) call a named function instead of re-deriving "does this hero hold X" and
// the raw number inline at every call site.

export function hasArtifact(h: Hero, line: ArtifactLine): boolean {
  return h.artifacts.E?.line === line || h.artifacts.R?.line === line;
}

/** Наплечник: a flat addition to the hero's own base max hp. Read wherever `h.def.hp` would otherwise mean
    "the cap" — heal()'s ceiling, a respawn's fresh hp, the bot's own retreat/shop-hurt thresholds — never
    where `h.def.hp` means the class's base stat for an unrelated formula. */
export function maxHpOf(h: Hero): number {
  // Бегемот's team buff (monsters.ts) scales the base stat, never the Наплечник flat bonus.
  return h.def.hp * (1 + h.boon.hpPct) + (hasArtifact(h, 'shoulder') ? ARTIFACTS.shoulder.hp : 0);
}

/** Точильный камень: attack cooldown is divided by this, not multiplied — a 12% faster attack means a
    shorter cooldown, not a longer one. 1 when not held, so every call site can multiply/divide
    unconditionally without its own branch. */
export function attackSpeedMultOf(h: Hero): number {
  return (hasArtifact(h, 'whetstone') ? 1 + ARTIFACTS.whetstone.atkSpeed : 1) + h.boon.atkSpeed;  // + Грифон's team buff
}

/** Сапоги гонца: an additive fraction of base move speed, meant to be summed alongside the haste buff the
    same way speedOf() already sums that — 0 when not held or while in combat (same `lastDamageAt` rule as
    Целебная мазь). The client's prediction restores `lastDamageAt` from the wire's `you.effects.quietFor`. */
export function passiveMoveSpeedBonusOf(h: Hero, now: number): number {
  return (bootsActiveOf(h, now) ? ARTIFACTS.boots.moveSpeed : 0) + h.boon.moveSpeed;  // + Грифон's team buff
}

function bootsActiveOf(h: Hero, now: number): boolean {
  return hasArtifact(h, 'boots') && now - h.lastDamageAt >= ARTIFACTS.boots.quiet;
}

/** Песочные часы: how many seconds of cooldown pass per second — skills and active artefacts, never the
    basic attack (that is Точильный камень's job). 1 when not held. */
export function cooldownRateOf(h: Hero): number {
  return hasArtifact(h, 'hourglass') ? 1 + ARTIFACTS.hourglass.cdRate : 1;
}

/** Туманный амулет: the chance a basic attack landing on this target misses — 0 for a monster or a hero
    without it. */
export function basicMissChanceOf(t: Target): number {
  return isHero(t) && hasArtifact(t, 'mistAmulet') ? ARTIFACTS.mistAmulet.missChance : 0;
}

/** Могильная свеча: the holder's respawn wait is multiplied by this. 1 when not held. */
export function respawnMultOf(h: Hero): number {
  return hasArtifact(h, 'graveCandle') ? 1 - ARTIFACTS.graveCandle.respawnCut : 1;
}

/** Зуб дракона: the damage multiplier `src` gets on `target` right now — enemy heroes above the hp line only. */
export function dragonToothMultOf(src: Hero | null, target: Target): number {
  if (!src || !hasArtifact(src, 'dragonTooth') || !isHero(target) || target.team === src.team) return 1;
  return target.hp > maxHpOf(target) * ARTIFACTS.dragonTooth.above ? 1 + ARTIFACTS.dragonTooth.dmg : 1;
}

/** Знамя: the capture-rate multiplier for a team capturing with these heroes on the point — no stacking. */
export function captureMultOf(capturers: readonly Hero[]): number {
  return capturers.some(h => hasArtifact(h, 'banner')) ? 1 + ARTIFACTS.banner.capture : 1;
}

/** Целебная мазь: hp/sec right now, or 0 — either the hero doesn't hold it, or it hasn't been quiet long
    enough yet. `now` is passed rather than read off `h` so this stays a pure function of the same (Game,
    Hero) shape as every other helper here. */
export function outOfCombatRegenOf(h: Hero, now: number): number {
  if (!hasArtifact(h, 'salve')) return 0;
  return now - h.lastDamageAt >= ARTIFACTS.salve.quiet ? ARTIFACTS.salve.regenPct * maxHpOf(h) : 0;
}

/* ---------------- per-hero state ---------------- */
export type ArtifactSlot = { line: ArtifactLine; cd: number };

/** Склянка крови's current charge as a share of max hp, from its slot's `cd` alone (so the wire, replay and
    prediction need nothing new): 0 right after drinking, `heal` once `cd` has run out. */
export function vialCharge(cd: number): number {
  const { cd: full, heal } = ARTIFACTS.bloodvial;
  return Math.min(heal, Math.max(0, (full - cd) / full * heal));
}

/** An active slot's own readiness, before the hero-state gates: off cooldown — or, for the charging
    Склянка, holding at least `minCharge`. Shared with the online client's cosmetic gate. */
export function artifactReady(s: ArtifactSlot): boolean {
  return s.line === 'bloodvial' ? vialCharge(s.cd) >= ARTIFACTS.bloodvial.minCharge - 1e-9 : s.cd <= 0;
}
// `by` is a hero id, not a Hero reference: a naive snapshot of this state must not drag a whole enemy Hero
// object along with every mark (kept an id so the server pass never has to work around it).
export type DamageMark = { pct: number; until: number; by: HeroId; source: 'brand'; totalDur: number };
export type TotemKind = 'ward' | 'wrath';
// `src` is a Hero reference like a Zone's: the snapshot builds its own fields and never sends it.
// `ticks` counts pulses left rather than comparing float times, so a totem always pulses exactly life/interval times.
export type Totem = { id: number; kind: TotemKind; pos: THREE.Vector3; team: Team; src: Hero; r: number; until: number; hp: number; tickAt: number; ticks: number };
export type ArtifactLock = { team: Team; line: ArtifactLine; until: number; ownerId: HeroId };

/* ---------------- casting (actives only) ---------------- */
type ActiveLine = 'bloodvial' | 'windoil' | 'cloak' | 'totemWard' | 'totemWrath' | 'brand' | 'tar';
const CAST: Record<ActiveLine, (g: Game, h: Hero, target: Target | Vec2 | null) => void> = {
  bloodvial(g, h) {
    // Exactly the accumulated charge, a share of MAXIMUM hp, including a held Наплечник — and routed through heal(), so it respects Смоляная
    // бутыль's healing-reduction debuff exactly like every other heal in the game, no special case needed.
    const s = h.artifacts.E?.line === 'bloodvial' ? h.artifacts.E : h.artifacts.R!;
    g.heal(h, maxHpOf(h) * vialCharge(s.cd));
    g.emit({ type: 'fx', name: 'bloodvial', hero: h.id });
  },
  windoil(g, h) {
    g.buff(h, 'haste', ARTIFACTS.windoil.haste, ARTIFACTS.windoil.dur, false);
    g.emit({ type: 'fx', name: 'windoil', hero: h.id });
  },
  cloak(g, h) {
    const dur = ARTIFACTS.cloak.dur;
    h.invulnUntil = Math.max(h.invulnUntil, g.t + dur);
    h.cloakUntil = Math.max(h.cloakUntil, g.t + dur);  // distinct from Финт's reuse of invulnUntil
    g.emit({ type: 'fx', name: 'cloak', hero: h.id });
  },
  totemWard(g, h) { placeTotem(g, h, 'ward'); },
  totemWrath(g, h) { placeTotem(g, h, 'wrath'); },
  brand(g, h, target) {
    const cfg = ARTIFACTS.brand;
    const t = target as Target;
    t.marks.push({ pct: cfg.pct, until: g.t + cfg.dur, by: h.id, source: 'brand', totalDur: cfg.dur });
    // A monster's or summon's id is not a hero id: its seal is a positional event, fog-gated like any other.
    if (!isHero(t)) g.emit({ type: 'fx', name: 'brandMonster', pos: g.pos2(t.pos) }, t);  // a summon's: as seen as the summon
    else g.emit({ type: 'fx', name: 'brand', hero: t.id });
  },
  tar(g, h, target) {
    const cfg = ARTIFACTS.tar;
    const p = toPoint(target as Target | Vec2), dir = dirTo(h.pos, p), len = Math.min(cfg.range, flatDist(h.pos, p));
    const at = h.pos.clone().addScaledVector(dir, len);
    g.pools.push({ pos: at, team: h.team, src: h, r: cfg.r, until: g.t + cfg.life, life: cfg.life, slow: cfg.slow, healReduction: cfg.healReduction, look: 'tar', tickAt: g.t });
    g.emit({ type: 'fx', name: 'shatter', pos: g.pos2(at) });
  },
};

export function canCastArtifact(g: Game, h: Hero, slot: ArtifactSlotKey): boolean {
  const s = h.artifacts[slot];
  if (!s || ARTIFACTS[s.line].kind !== 'active') return false;  // a passive has no button
  if (!h.alive || g.t < h.stunUntil || h.state !== 'normal' || g.taunted(h) || g.forcedKind(h) || h.pull) return false;
  if (g.t < h.cloakUntil) return false;  // Плащ: an escape, not a free window to also cast something else
  return artifactReady(s);
}

export function castArtifact(g: Game, h: Hero, slot: ArtifactSlotKey, target: Target | Vec2 | null): boolean {
  if (!canCastArtifact(g, h, slot)) return false;
  const s = h.artifacts[slot] as ArtifactSlot;
  const def = ARTIFACTS[s.line] as (typeof ARTIFACTS)[ActiveLine];
  if (def.targeting === 'point' && !target) return false;
  if (def.targeting === 'enemy') {
    // A live enemy hero, or a live monster not leashing home (immune then, see damageMonster). Reach is an
    // attack's: centre distance minus the monster's bulk. input.ts's enemyCastProblem mirrors this.
    if (!isUnit(target) || !target.alive || flatDist(h.pos, target.pos) > def.range + bulkOf(target)) return false;
    if (isMonster(target) ? target.state === 'return' : target.team === h.team || (isHero(target) && g.silhouette(target))) return false;
  }
  g.stir(h);
  CAST[s.line as ActiveLine](g, h, target);
  s.cd = def.cd;  // Склянка: emptied, recharging from zero
  return true;
}

/* ---------------- shop: buy / sell, base-only, team-locked ---------------- */
export const SHOP = {
  price: 150,
  sellRefundPct: 0.5,
  lockSeconds: 45,
} as const;

// "On own base floor": the doc gives no separate number for this, so it reuses the base's one other
// radius-shaped constant, the fountain's healing circle (RULES.fountainRadius), rather than inventing one.
export function inOwnBase(h: Hero): boolean {
  const [bx, bz] = LAYOUT.bases[h.team];
  return flatDist(h.pos, { x: bx, z: bz }) < RULES.fountainRadius;
}

function lineLockedFor(g: Game, h: Hero, line: ArtifactLine): boolean {
  return g.artifactLocks.some(l => l.team === h.team && l.line === line && l.until > g.t && l.ownerId !== h.id);
}

export function buyArtifact(g: Game, h: Hero, slot: ArtifactSlotKey, line: ArtifactLine): boolean {
  if (!h.alive || !inOwnBase(h) || h.artifacts[slot]) return false;
  if (h.artifacts.E?.line === line || h.artifacts.R?.line === line) return false;  // no duplicate across own slots
  if (lineLockedFor(g, h, line)) return false;
  // The team's shared pool, first come first served: two teammates buying on one tick resolve in command
  // order (the server drains seats 0..3, which is also each team's own internal turn order).
  if (g.ink[h.team] < SHOP.price) return false;
  g.ink[h.team] -= SHOP.price;
  // A fresh purchase is ready at once, active or passive (a passive's 0 is inert — canCastArtifact refuses
  // it a button at all); for Склянка крови that means bought full, at its cap. Sell-then-rebuy does reset an active's cooldown, but only at base and for 75 ink.
  h.artifacts[slot] = { line, cd: 0 };
  g.stats.bought(h.id, SHOP.price);
  g.artifactLocks = g.artifactLocks.filter(l => l.until > g.t);  // prune expired locks so they can't be mistaken for live ones
  // A STILL-ACTIVE lock this same hero already owns for this line is never reset by them re-buying it —
  // only a genuinely new owner, a different line, or a lock that has actually expired restarts the 45s.
  const ownLiveLock = g.artifactLocks.find(l => l.team === h.team && l.line === line && l.ownerId === h.id);
  if (!ownLiveLock) {
    g.artifactLocks = g.artifactLocks.filter(l => !(l.team === h.team && l.line === line));
    g.artifactLocks.push({ team: h.team, line, until: g.t + SHOP.lockSeconds, ownerId: h.id });
  }
  return true;
}

export function sellArtifact(g: Game, h: Hero, slot: ArtifactSlotKey): boolean {
  if (!h.alive || !inOwnBase(h)) return false;
  const s = h.artifacts[slot];
  if (!s) return false;
  const refund = Math.floor(SHOP.price * SHOP.sellRefundPct);
  g.ink[h.team] += refund;
  g.stats.sold(h.id, refund);
  h.artifacts[slot] = null;
  // Selling Наплечник can drop maxHpOf(h) out from under a hero topped up past the new, lower cap — heal()
  // only ever clamps DURING a heal, never retroactively, so without this a sold-off Наплечник would leave
  // the hero walking around above their own max (a health bar reading over 100%) until their next hit.
  if (h.hp > maxHpOf(h)) h.hp = maxHpOf(h);
  return true;
}

/* ---------------- Тотем Стойкости / Тотем Гнева: destructible world objects ---------------- */
const TOTEM_LINE = { ward: 'totemWard', wrath: 'totemWrath' } as const;
export const totemDef = (kind: TotemKind) => ARTIFACTS[TOTEM_LINE[kind]];

function placeTotem(g: Game, h: Hero, kind: TotemKind) {
  const cfg = totemDef(kind);
  g.totems = g.totems.filter(t => !(t.src === h && t.kind === kind));  // one of each per hero: a recast replaces
  const pos = h.pos.clone();
  g.totems.push({
    id: g.nextTotemId++, kind, pos, team: h.team, src: h, r: cfg.r, until: g.t + cfg.life, hp: cfg.hp,
    tickAt: g.t, ticks: Math.round(cfg.life / cfg.interval),
  });
  g.emit({ type: 'fx', name: 'totemPlace', pos: g.pos2(pos), hero: h.id, kind });
}

/* Each pulse: Стойкости heals allies through heal() (Бессонница, Смоляная бутыль apply; healing is credited
   to the owner), Гнева hits enemies through damage() as skill damage (shields, dodges, kills and assists
   credited to the owner). Heroes only — a monster is neither healed nor hurt by a totem. */
export function updateTotems(g: Game) {
  g.totems = g.totems.filter(t => t.until > g.t && t.hp > 0);
  for (const t of g.totems) {
    if (t.kind === 'wrath') g.stir(t.src);  // Без отражения: no silhouette while her own Тотем Гнева still hits
    if (t.ticks <= 0 || g.t < t.tickAt) continue;
    const cfg = totemDef(t.kind);
    t.tickAt = g.t + cfg.interval;
    t.ticks--;
    for (const o of g.heroes) {
      if (!o.alive || flatDist(o.pos, t.pos) > t.r) continue;
      const amount = maxHpOf(o) * cfg.pct * cfg.interval;
      if (t.kind === 'ward' && o.team === t.team) g.heal(o, amount, t.src);
      else if (t.kind === 'wrath' && o.team !== t.team) g.damage(o, amount, t.src, { skill: true, fromPos: t.pos, by: null });  // Стойка faces the totem, not its owner
    }
  }
}

/* A basic attack's hit on an enemy totem (game.ts resolveAttackTotem / its homing bolt): one of its `hp`. */
export function strikeTotem(g: Game, totemId: number, by: Hero): boolean {
  const t = g.totems.find(x => x.id === totemId && x.team !== by.team);
  if (!t) return false;
  t.hp -= 1;
  if (t.hp <= 0) { g.emit({ type: 'fx', name: 'totemBreak', pos: g.pos2(t.pos), kind: t.kind }); g.totems = g.totems.filter(x => x !== t); }
  else g.emitHit({ type: 'fx', name: 'hit', pos: g.pos2(t.pos), color: 0x999999 }, undefined, by);
  return true;
}

/* ---------------- bots: buying ---------------- */

/** Each artefact's purpose, checked in priority order — a counter-pick for what the enemy is actually
    running, or a catch-up tool for a losing scoreline, is worth more than a generic default, so those come
    first; the hero's own role only breaks what's left. Named branches, one line each, over anything scored
    or weighted — simple and legible was the brief. No "upgrade" branch any more: an artefact is bought
    whole, once, and that is the entire decision. */
function chooseArtifactLine(g: Game, h: Hero): ArtifactLine | null {
  const already = new Set<ArtifactLine>([h.artifacts.E?.line, h.artifacts.R?.line].filter((l): l is ArtifactLine => !!l));
  // A line a teammate just bought is locked for 45 s: wanting it anyway means walking home to a purchase
  // that cannot go through, re-deciding the same way next think tick, and repeating the trip. Skipping it
  // simply moves the choice down the list — a bot never waits out a lock it has an alternative to.
  const wants = (line: ArtifactLine) => !already.has(line) && !lineLockedFor(g, h, line);
  const enemies = g.heroes.filter(e => e.team !== h.team);
  const enemySupports = enemies.filter(e => e.def.role === 'support').length;
  const otherTeam: Team = h.team === 'a' ? 'b' : 'a';
  const behind = g.score[h.team] < g.score[otherTeam];

  const counter = counterPick(g, h, enemies, wants);
  if (counter) return counter;
  if (enemySupports >= 2 && wants('tar')) return 'tar';                 // deny the enemy's own healing
  if (h.def.role === 'support' && wants('cloak')) return 'cloak';       // a support's own escape — it cannot out-duel a dive
  if (behind && wants('brand')) return 'brand';                         // a losing team needs to force, and win, a fight
  if (behind && wants('banner')) return 'banner';                       // ...and to turn the points back faster
  if (h.def.role === 'guard' && wants('totemWrath')) return 'totemWrath';  // guards stand in the brawl; the enemy around them pays
  if (h.def.role === 'support' && wants('totemWard')) return 'totemWard';  // a support's second tool: sustain for the whole fight
  if (h.def.role === 'shooter' && wants('bloodvial')) return 'bloodvial';  // the squishiest ranged class; a panic heal fits it best
  if (h.def.role === 'shooter' && wants('dragonTooth')) return 'dragonTooth';  // opens fights from range, on still-healthy targets
  if (h.def.role === 'duelist' && wants('windoil')) return 'windoil';      // a diver's own stick-or-disengage burst
  if (h.def.role === 'duelist' && wants('mistAmulet')) return 'mistAmulet';  // trades blows up close: every whiffed swing counts
  if (h.def.role === 'guard' && wants('hourglass')) return 'hourglass';      // a guard's control skills, back sooner
  // Default fallback, for whichever slot the branches above didn't already fill:
  for (const line of ['whetstone', 'shoulder', 'boots', 'salve', 'hourglass', 'mistAmulet', 'graveCandle', 'dragonTooth', 'banner', 'tar', 'cloak', 'brand', 'totemWard', 'totemWrath', 'bloodvial', 'windoil'] as ArtifactLine[]) {
    if (wants(line)) return line;
  }
  return null;
}

/* Answers to what the enemy actually fields, ahead of the role defaults: Туманный амулет for whoever stands in
   reach of two basic-attack carries (duelists, shooters); Смоляная бутыль for a fighter facing a healer or
   Сердце Гидры; a support whose teammate fights up close sets Тотем Стойкости down first. */
function counterPick(g: Game, h: Hero, enemies: Hero[], wants: (line: ArtifactLine) => boolean): ArtifactLine | null {
  const basicCarries = enemies.filter(e => e.def.role === 'duelist' || e.def.role === 'shooter').length;
  const heals = enemies.some(e => e.def.role === 'support') || g.relics[h.team === 'a' ? 'b' : 'a']?.line === 'hydraHeart';
  const mate = g.heroes.find(a => a !== h && a.team === h.team);
  if (basicCarries >= 2 && h.def.role !== 'support' && wants('mistAmulet')) return 'mistAmulet';
  if (heals && (h.def.role === 'guard' || h.def.role === 'gamechanger') && wants('tar')) return 'tar';
  if (h.def.role === 'support' && mate?.def.melee && wants('totemWard')) return 'totemWard';
  return null;
}

/** What a hero would buy right now, or null if there is genuinely nothing worth doing — there is no
    "upgrade what I have" branch any more, so once both slots are filled a hero simply never shops again. */
function chooseShopAction(g: Game, h: Hero): { slot: ArtifactSlotKey; line: ArtifactLine } | null {
  const openSlot: ArtifactSlotKey | null = !h.artifacts.E ? 'E' : !h.artifacts.R ? 'R' : null;
  if (!openSlot) return null;
  const line = chooseArtifactLine(g, h);
  return line && canAffordFromPool(g, h) ? { slot: openSlot, line } : null;
}

const heldCount = (h: Hero) => (h.artifacts.E ? 1 : 0) + (h.artifacts.R ? 1 : 0);

/** The pool is shared, so a bot yields to a teammate holding fewer artefacts — unless the pool already
    covers both of them, which also keeps a human who never shops from starving the bot forever. */
function canAffordFromPool(g: Game, h: Hero): boolean {
  const pool = g.ink[h.team];
  if (pool < SHOP.price) return false;
  if (pool >= 2 * SHOP.price) return true;
  return g.heroes.every(o => o === h || o.team !== h.team || heldCount(o) >= heldCount(h));
}

/** Whether this is a moment worth walking home for — the shop is base-only, so the trip has a real cost,
    and that cost is the actual decision: never mid-fight or while already retreating for survival (that
    reads as combat priority, not a shopping choice); never with nothing worth buying (`relicWanted`: a relic
    — relics.ts's botRelicChoice — is); a free stop if
    already standing at the fountain regardless of health; otherwise only when the walk is on this hero's
    own side of the map AND there is a second reason (being hurt) to make the special trip at all — a bot
    topped up on health that's merely rich in ink can afford to wait for a more efficient moment. */
export function botShouldShop(g: Game, h: Hero, foes: Hero[], relicWanted = false): boolean {
  if (foes.length || h.hp < maxHpOf(h) * 0.3) return false;
  if (!relicWanted && !chooseShopAction(g, h)) return false;
  const base = LAYOUT.bases[h.team];
  if (flatDist(h.pos, { x: base[0], z: base[1] }) < RULES.fountainRadius) return true;
  const enemyBase = LAYOUT.bases[h.team === 'a' ? 'b' : 'a'];
  const walkAffordable = flatDist(h.pos, { x: base[0], z: base[1] }) < flatDist(h.pos, { x: enemyBase[0], z: enemyBase[1] });
  const hurt = h.hp < maxHpOf(h) * 0.8;
  return walkAffordable && hurt;
}

/** Spends once, having arrived — game.ts's think() calls this only once the walk from botShouldShop above
    is actually complete. */
export function botShop(g: Game, h: Hero) {
  const action = chooseShopAction(g, h);
  if (action) g.buyArtifact(h, action.slot, action.line);
}

/* ---------------- bots: casting (actives only — a passive has no button to press) ---------------- */

/** Same shape as botBrain.ts's botSkills (an `ai` hint per entry, tried in slot order, a canCast/cooldown
    gate, an occasional skip so a bot doesn't fire the instant a window opens) but each line gets its own
    bespoke trigger rather than a shared generic dispatch — a panic heal, a mobility burst, a chase, a
    contested point and a team committing are genuinely different shapes, not variations on one.
    `canCastArtifact` already refuses a passive slot a button at all, so this never needs to check
    `kind` itself. */
export function botArtifacts(g: Game, h: Hero, foe?: Hero) {
  if (h.state !== 'normal') return;
  const d = foe ? flatDist(h.pos, foe.pos) : Infinity;
  for (const slot of ['E', 'R'] as ArtifactSlotKey[]) {
    const s = h.artifacts[slot];
    if (!s || !canCastArtifact(g, h, slot) || g.rng() < 0.35) continue;
    let target: Hero | Vec2 | null | undefined;
    if (s.line === 'bloodvial') {
      const ai = ARTIFACTS.bloodvial.ai, share = h.hp / maxHpOf(h), charge = vialCharge(s.cd);
      if (!((share < ai.low && charge >= ai.lowCharge) || (share < ai.panic && charge >= ai.panicCharge))) continue;
      target = null;
    } else if (s.line === 'windoil') {
      if (!foe || d >= ARTIFACTS.windoil.ai.near) continue;
      target = null;
    } else if (s.line === 'cloak') {
      const ai = ARTIFACTS.cloak.ai;
      if (!foe || h.hp >= maxHpOf(h) * ai.low || d >= ai.chased) continue;
      target = null;
    } else if (s.line === 'tar') {
      const pt = g.points.find(p => p.contested);
      if (!pt || flatDist(h.pos, pt.pos) > ARTIFACTS.tar.range) continue;
      target = { x: pt.pos.x, z: pt.pos.z };
    } else if (s.line === 'totemWard') {
      const cfg = ARTIFACTS.totemWard;
      const hurt = g.heroes.some(a => a.team === h.team && a.alive && flatDist(a.pos, h.pos) <= cfg.r && a.hp < maxHpOf(a) * cfg.ai.hurt);
      if (!foe || !hurt) continue;
      target = null;
    } else if (s.line === 'totemWrath') {
      const near = ARTIFACTS.totemWrath.ai.near;
      if (!g.heroes.some(e => e.team !== h.team && e.alive && g.visibleTo(h.team, e) && flatDist(e.pos, h.pos) <= near)) continue;
      target = null;
    } else if (s.line === 'brand') {
      const ai = ARTIFACTS.brand.ai;
      const committing = !!foe && g.heroes.some(a => a !== h && a.team === h.team && a.alive && flatDist(a.pos, foe.pos) < ai.withAlly);
      if (!foe || !committing || d > ARTIFACTS.brand.range) continue;
      target = foe;
    } else continue;  // a passive slot never reaches here — canCastArtifact already refused it above
    if (castArtifact(g, h, slot, target ?? null)) return;
  }
}
